import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BookOpen, Award, CreditCard, ArrowRight, Trophy } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [certs, setCerts] = useState([]);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    api.get("/api/student/enrollments").then(({ data }) => setEnrollments(data)).catch(() => {});
    api.get("/api/student/certificates").then(({ data }) => setCerts(data)).catch(() => {});
    api.get("/api/payments/history").then(({ data }) => setPayments(data)).catch(() => {});
  }, []);

  const stats = [
    { label: "Enrolled courses", value: enrollments.length, icon: BookOpen, color: "#D92D20" },
    { label: "Certificates", value: certs.length, icon: Award, color: "#064E3B" },
    { label: "Purchases", value: payments.filter((p) => p.status === "paid").length, icon: CreditCard, color: "#D97706" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="student-dashboard">
      <div className="label-eyebrow">Student dashboard</div>
      <h1 className="font-display font-black text-3xl md:text-4xl mt-1">Welcome, {user?.full_name?.split(" ")[0]}.</h1>
      <p className="text-[#4B5563] mt-1">Pick up where you left off.</p>

      <div className="grid sm:grid-cols-3 gap-4 mt-8">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white border border-[#E5E7EB] rounded-xl p-5">
              <div className="flex items-center justify-between">
                <div className="label-eyebrow">{s.label}</div>
                <Icon className="w-5 h-5" style={{ color: s.color }} />
              </div>
              <div className="font-display font-black text-4xl mt-3">{s.value}</div>
            </div>
          );
        })}
      </div>

      <section className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-2xl">My Courses</h2>
          <Link to="/courses" className="text-sm font-semibold text-[#D92D20] hover:underline" data-testid="link-browse-more">Browse more →</Link>
        </div>
        {enrollments.length === 0 ? (
          <div className="bg-white border border-dashed border-[#E5E7EB] rounded-xl p-10 text-center">
            <p className="text-[#4B5563]">You haven't enrolled in any course yet.</p>
            <Link to="/courses" className="btn-primary inline-block mt-4" data-testid="empty-browse">Browse courses</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {enrollments.map(({ enrollment, course }) => (
              <div key={enrollment.id} className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden flex" data-testid={`enrolled-${course.slug}`}>
                <img src={course.thumbnail_url} alt={course.title} className="w-32 object-cover" />
                <div className="p-4 flex-1">
                  <div className="label-eyebrow">{course.category_name} · {course.level}</div>
                  <div className="font-display font-bold text-lg leading-tight mt-1">{course.title}</div>
                  <div className="mt-3 h-2 rounded-full bg-[#F3F4F6] overflow-hidden">
                    <div className="h-full bg-[#D92D20]" style={{ width: `${enrollment.completion_percent}%` }} />
                  </div>
                  <div className="text-xs text-[#4B5563] mt-1.5">{enrollment.completion_percent}% complete</div>
                  <Link to={`/learn/${course.id}`} className="text-sm font-semibold text-[#D92D20] mt-3 inline-flex items-center gap-1" data-testid={`continue-${course.slug}`}>
                    {enrollment.completed ? "Review course" : "Continue"} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-12">
        <h2 className="font-display font-bold text-2xl mb-4">My Certificates</h2>
        {certs.length === 0 ? (
          <div className="text-sm text-[#4B5563]">Complete a course to earn your first certificate.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {certs.map((c) => (
              <div key={c.id} className="bg-white border border-[#E5E7EB] rounded-xl p-5 flex items-center gap-4">
                <Trophy className="w-10 h-10 text-[#D97706]" />
                <div className="flex-1">
                  <div className="font-display font-bold">{c.course_title}</div>
                  <div className="text-xs text-[#4B5563] font-mono">{c.cert_number}</div>
                  <div className="text-xs text-[#4B5563]">Issued {c.issued_at.slice(0,10)}</div>
                </div>
                <Link to={`/certificate/${c.id}`} className="btn-outline text-sm" data-testid={`view-cert-${c.id}`}>View</Link>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-12">
        <h2 className="font-display font-bold text-2xl mb-4">Purchase History</h2>
        {payments.length === 0 ? (
          <div className="text-sm text-[#4B5563]">No purchases yet.</div>
        ) : (
          <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F3F4F6] text-[#111827]">
                <tr><th className="px-4 py-3 text-left">Course</th><th className="px-4 py-3 text-left">Amount</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Date</th></tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-[#E5E7EB]">
                    <td className="px-4 py-3">{p.course_title}</td>
                    <td className="px-4 py-3">${p.amount.toFixed(2)} {p.currency.toUpperCase()}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold uppercase px-2 py-1 rounded ${p.status === "paid" ? "bg-[#064E3B]/10 text-[#064E3B]" : "bg-[#F3F4F6] text-[#4B5563]"}`}>{p.status}</span>
                    </td>
                    <td className="px-4 py-3 text-[#4B5563]">{p.created_at.slice(0,10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
